import DataListURLTable, {
  ITableColumnURL,
} from "shared/components/dataListURL/DataListURLTable";
import { useWindowSize } from "shared/hooks/useWindowSize";
import { TableActionsContextProvider } from "shared/components/HoverActionsVirtualTable/TableActionsContext";
import { ContextTableActions } from "shared/components/tableActions/ContextTableActions";
import { InlineTableActions } from "shared/components/tableActions/InlineTableActions";
import {
  CalendarOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  MailOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ToolbarButton } from "shared/components/toolbarTable/ToolbarTable";
import { SearchFilterDropdown } from "shared/components/SearchFilterDropdown";
import { MarketingMaterialTableItem } from "shared/types/marketingMaterials";
import { marketingMaterialFieldsKeys } from "./marketingMaterialsTable/fields";
import Tags from "screens/designStudio/shared/Tags";
import {
  customizableMap,
  fields,
} from "screens/designStudio/templates/fileDrawer/FormFields.schema";
import { useMemo } from "react";
import { StateKey } from "shared/constants/states";
import { DisabledPrintDeliveryMessage, ROUTES } from "./utils/constants";
import useNavigateWithSearch from "shared/hooks/useNavigateWithSearch";
import { useMarketingMaterialActions } from "./hooks/useMarketingMaterialActions";
import { deliveryMethods } from "screens/designStudio/utils";
import { uniq, upperCase } from "lodash";
import NameCardCell from "shared/components/NameCardCell";
import moment from "moment-timezone";
import EmptyMarketingMaterials from "./EmptyMarketingMaterials";
import { getFiltersFromData } from "utils/helpers.table";
import type { Language } from "shared/types/salesEnablement";
import { DateFilterDropdown } from "shared/components/DateFilterDropdown";
import { TimeAgo } from "shared/components/TimeAgo";
import { Languages } from "utils/helpers.salesEnablement";
import { TIMEZONE } from "screens/designStudio/fields";
import { useIsSalesEnablementAdmin } from "shared/hooks/useIsAdmin";

type Props = {
  data: MarketingMaterialTableItem[];
  originalData: MarketingMaterialTableItem[];
  loading: boolean;
  isPdfLoading: boolean;
  onEmail: (selectedItems: MarketingMaterialTableItem[]) => void;
  onDownload: (selectedItems: MarketingMaterialTableItem[]) => void;
};

const MarketingMaterialsTable = ({
  data,
  loading,
  originalData,
  isPdfLoading,
  onEmail,
  onDownload,
}: Props) => {
  const isSalesEnablementAdmin = useIsSalesEnablementAdmin();
  const { windowInnerHeight } = useWindowSize();
  const navigateWithSearch = useNavigateWithSearch();
  const { onDuplicate, onDelete, isCreating, isDeleting } =
    useMarketingMaterialActions();

  const filtersData = useMemo(() => {
    const { locationsSet, audienceSet } = originalData.reduce(
      (acc, item) => {
        item.locations?.forEach(state => acc.locationsSet.add(state));
        item.templateAudience?.forEach(audience =>
          acc.audienceSet.add(audience),
        );
        return acc;
      },
      {
        locationsSet: new Set<StateKey>(),
        audienceSet: new Set<string>(),
      },
    );

    return {
      locations: [...locationsSet].sort(),
      audience: [...audienceSet].sort(),
    };
  }, [originalData]);
  const getFilters = getFiltersFromData(originalData);

  const columns: ITableColumnURL<
    keyof MarketingMaterialTableItem,
    MarketingMaterialTableItem
  >[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 280,
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
      render: (_, materialItem) => {
        return (
          <NameCardCell
            name={materialItem.name}
            thumbnail={materialItem.templateThumbnail}
            onCellClick={() => navigateWithSearch(materialItem.id)}
          />
        );
      },
    },
    {
      key: "templateDeliveryMethods",
      title: "Delivery Method",
      width: 200,
      dataIndex: ["deliveryMethods"],
      filters: deliveryMethods.map(method => ({
        text: method.label,
        value: method.value,
      })),
      render: (_, material) => {
        return (
          <Tags
            tags={
              uniq(material?.templateDeliveryMethods)?.map(item => ({
                id: item + material.id,
                name:
                  deliveryMethods.find(opt => opt.value === item)?.label ||
                  item,
              })) ?? []
            }
          />
        );
      },
    },
    {
      title: "States",
      dataIndex: "locations",
      key: "locations",
      width: 200,
      filters: filtersData.locations.map(state => ({
        text: state,
        value: state,
      })),
      render: (_, material) => (
        <Tags
          tags={material.locations?.map(state => ({
            id: state,
            name: state,
          }))}
        />
      ),
    },
    {
      key: "templateCustomizable",
      title: "Customizable",
      dataIndex: "templateCustomizable",
      width: 200,
      filters: fields.customizable.options.map(item => ({
        text: item.label,
        value: item.value,
      })),
      render: (_, material) =>
        material.templateCustomizable
          ? customizableMap.customizable
          : customizableMap.nonCustomizable,
    },
    {
      title: "Audience",
      dataIndex: "audience",
      key: "templateAudience",
      width: 200,
      filters: filtersData.audience.map(audience => ({
        text: fields.audience.options.find(item => item.value === audience)
          ?.label,
        value: audience,
      })),
      render: (_, material) => (
        <Tags
          tags={material.templateAudience?.map(item => ({
            id: item,
            name:
              fields.audience.options.find(opt => opt.value === item)?.label ||
              item,
          }))}
        />
      ),
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      width: 150,
      render: upperCase,
      filters: getFilters("language", {
        textMapper: value => Languages[value as Language].label,
        sorted: true,
      }),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date: Date) => <TimeAgo date={date} />,
      filterDropdown: DateFilterDropdown,
      filterIcon: (filtered: boolean) => (
        <CalendarOutlined color={filtered ? "#1890ff" : undefined} />
      ),
    },
    {
      title: "Last Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (date: Date) => <TimeAgo date={date} />,
      filterDropdown: DateFilterDropdown,
      filterIcon: (filtered: boolean) => (
        <CalendarOutlined color={filtered ? "#1890ff" : undefined} />
      ),
    },
    ...(isSalesEnablementAdmin
      ? [
          {
            title: "Expiration Date",
            dataIndex: "templateExpirationDate",
            key: "templateExpirationDate",
            width: 150,
            render: (_, material) =>
              moment(material.templateExpirationDate)
                .tz(TIMEZONE)
                .format("MM/YY"),
            filterDropdown: DateFilterDropdown,
            filterIcon: (filtered: boolean) => (
              <CalendarOutlined color={filtered ? "#1890ff" : undefined} />
            ),
          } as ITableColumnURL<
            keyof MarketingMaterialTableItem,
            MarketingMaterialTableItem
          >,
        ]
      : []),
  ];

  const generateToolbarContents: (
    record?: MarketingMaterialTableItem,
  ) => ToolbarButton = record => {
    if (!record) return {};
    const disabledOrderMessage =
      record.templateErrorStatus?.message ??
      record.materialErrorStatus?.message;
    return {
      Edit: {
        icon: <EditOutlined />,
        onClick: () => {
          navigateWithSearch(ROUTES.edit(record.id));
        },
        showInContextMenu: true,
        showInInlineMenu: true,
      },
      Delete: {
        icon: <DeleteOutlined />,
        onClick: () => {
          onDelete({
            ids: [record.id],
            mode: "optimistic",
          });
        },
        showInContextMenu: true,
        showInInlineMenu: true,
        loading: isDeleting,
      },
      Duplicate: {
        icon: <CopyOutlined />,
        onClick: () => {
          onDuplicate({
            material: record,
            mode: "optimistic",
          });
        },
        showInContextMenu: true,
        showInInlineMenu: true,
        disabled: !!record.templateErrorStatus,
        loading: isCreating,
        extraInfo: {
          tooltip: record.templateErrorStatus?.message ?? "Duplicate",
        },
      },
      DownloadPDF: {
        showInContextMenu: true,
        showInInlineMenu: true,
        disabled:
          !record?.templateDeliveryMethods?.includes("download") ||
          !!disabledOrderMessage ||
          isPdfLoading,
        onClick: () => {
          onDownload([record]);
        },
        icon: <DownloadOutlined />,
        extraInfo: {
          title: "Download",
          tooltipText: disabledOrderMessage ?? "Download",
        },
      },
      CustomButton: {
        showInContextMenu: true,
        showInInlineMenu: true,
        onClick: () => {
          onEmail([record]);
        },
        icon: <MailOutlined />,
        disabled:
          !record?.templateDeliveryMethods?.includes("email") ||
          !!disabledOrderMessage,
        extraInfo: {
          title: "Send by Email",
          text: "Send by Email",
          tooltipText: disabledOrderMessage ?? "Email",
        },
      },
      Print: {
        showInContextMenu: true,
        showInInlineMenu: true,
        icon: <PrinterOutlined />,
        disabled: true,
        extraInfo: {
          title: "Print",
          text: "Print",
          tooltipText: DisabledPrintDeliveryMessage,
          tooltipPlacement: "topRight",
        },
      },
    };
  };

  return (
    <TableActionsContextProvider<MarketingMaterialTableItem>
      renderContextActions={record => (
        <ContextTableActions
          getItemsToRender={generateToolbarContents}
          record={record}
        />
      )}
      renderHoverActions={record => (
        <InlineTableActions
          getItemsToRender={generateToolbarContents}
          record={record}
        />
      )}
    >
      <DataListURLTable<
        keyof MarketingMaterialTableItem,
        MarketingMaterialTableItem
      >
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={false}
        locale={{
          emptyText: <EmptyMarketingMaterials />,
        }}
        size="small"
        scroll={{ y: windowInnerHeight - 245 }}
        type="hoverActionsVirtualTable"
        fieldKeys={marketingMaterialFieldsKeys}
      />
    </TableActionsContextProvider>
  );
};

export default MarketingMaterialsTable;
